import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges, OnChanges, Renderer2, OnDestroy, ElementRef, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/platform-browser';
import { HasSubscriptions } from '../../has-subscriptions';
import {environment} from "../../../../environments/environment-wrapper";

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent extends HasSubscriptions implements OnInit, OnChanges, OnDestroy  {
  @Input() readonly totalPages: number;
  @Input() readonly currentPageNumber: number;
  @Input() readonly showTextPages: boolean = true;
  @Input() readonly disableSEO: boolean = false;

  @Output() onChangePage: EventEmitter<number> = new EventEmitter();
  constructor(
    private _renderer: Renderer2,
    private _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document
  ) {super(); }

  baseUrl: string;      // The base URL that all the links should be pointed to

  // References to the next and prev link tags in the head of the document
  private static prevLinkRef;
  private static nextLinkRef;

  ngOnInit() {
    // We use the snapshot url to get the URL for SEO purposes
    // We subsribe to the route in case they stay on a same route but change a sub folder portion of the route
    this.addSub(this._activatedRoute.url.subscribe((url) => {
      this.baseUrl = '/' + url.join('/');
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.disableSEO && (changes.currentPageNumber || changes.totalPages)) {
      // Update the Link tags in the head to point to the correct pages
      this.updateLinkTags();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    // We need to remove the Link tags
    this.removeLinkTags();
  }

  changePage(newPage: number, $event) {
    $event.preventDefault();
    $event.target.blur();
    this.onChangePage.emit(newPage);
  }

  /**
   * Adds/Updates the link tags in the head element to point to the correct next/prev pages for pagination purposes
   */
  updateLinkTags() {
    if (this.totalPages > 1) {
      // We only add the next page if there is more than 1 page, and not on the last page
      if (this.currentPageNumber < this.totalPages - 1) {
        // Check if the link already exists
        const linkTagNext = PaginationComponent.nextLinkRef ? PaginationComponent.nextLinkRef : this._renderer.createElement('link');
        this._renderer.setAttribute(linkTagNext, 'rel', 'next');
        this._renderer.setAttribute(linkTagNext, 'href', this.baseUrl + '?page=' + (this.currentPageNumber + 2));
        if (!PaginationComponent.nextLinkRef) {
          PaginationComponent.nextLinkRef = linkTagNext;
          this._renderer.appendChild(this.document.head, linkTagNext);
        }
      }
      else if (PaginationComponent.nextLinkRef) this.removeNextTag();
      // We only add the prev page if there is more than 1 page, and not on the first page
      if (this.currentPageNumber > 0) {
        // Check if the link already exists
        const linkTagPrev = PaginationComponent.prevLinkRef ? PaginationComponent.prevLinkRef : this._renderer.createElement('link');
        this._renderer.setAttribute(linkTagPrev, 'rel', 'prev');
        this._renderer.setAttribute(linkTagPrev, 'href', this.baseUrl + '?page=' + (this.currentPageNumber));
        if (!PaginationComponent.prevLinkRef) {
          PaginationComponent.prevLinkRef = linkTagPrev;
          this._renderer.appendChild(this.document.head, linkTagPrev);
        }
      }
      else if (PaginationComponent.prevLinkRef) this.removePrevTag();
    }
    else {
      this.removeLinkTags();
    }
  }

  /**
   * make sure to remove the link tags so that on pages without pagination they are not present
   */
  private removeLinkTags() {
    if (PaginationComponent.prevLinkRef) this.removePrevTag();
    if (PaginationComponent.nextLinkRef) this.removeNextTag();
  }

  private removeNextTag() {
    this._renderer.removeChild(this.document.head, PaginationComponent.nextLinkRef);
    PaginationComponent.nextLinkRef = null;
  }

  private removePrevTag() {
    this._renderer.removeChild(this.document.head, PaginationComponent.prevLinkRef);
    PaginationComponent.prevLinkRef = null;
  }
}
